"use client";

import { useEffect, useRef, useState, RefObject } from "react";

interface UseScrollRevealOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
  enabled?: boolean;
}

/**
 * Hook for scroll-triggered animations using Intersection Observer
 * 
 * @param options Configuration options
 * @returns Object with ref to attach to element and isVisible boolean
 * 
 * @example
 * ```tsx
 * const { ref, isVisible } = useScrollReveal({ threshold: 0.1 });
 * return <div ref={ref} className={isVisible ? 'animate-visible' : 'animate-initial'}>Content</div>
 * ```
 */
export function useScrollReveal({
  threshold = 0.1,
  rootMargin = "0px",
  once = true,
  enabled = true,
}: UseScrollRevealOptions = {}): {
  ref: RefObject<HTMLElement>;
  isVisible: boolean;
} {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!enabled || !element) {
      return;
    }

    // Check if user prefers reduced motion
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      // Immediately show element if reduced motion is preferred
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) {
            setHasAnimated(true);
          }
        } else if (!once) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, rootMargin, once, enabled]);

  // If already animated and once=true, keep visible
  const finalIsVisible = once && hasAnimated ? true : isVisible;

  return { ref, isVisible: finalIsVisible };
}
